import {
  Firewall,
  Operation,
  type CreateFirewallRequest,
  type FirewallRuleInput,
  type FirewallTargets,
  type UpdateFirewallRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";
import { keys } from "./queries.js";

/** The project's firewalls; live updates refresh them through the `projects` key. */
export const useFirewalls = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "firewalls"],
    queryFn: () => api(`/projects/${projectId}/firewalls`, { schema: z.array(Firewall) }),
  });

export const useFirewall = (projectId: string, firewallId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "firewalls", firewallId],
    queryFn: () => api(`/projects/${projectId}/firewalls/${firewallId}`, { schema: Firewall }),
  });

function useProjectWrite<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}

export const useCreateFirewall = (projectId: string) =>
  useProjectWrite((body: CreateFirewallRequest) =>
    api(`/projects/${projectId}/firewalls`, {
      method: "POST",
      body,
      schema: Firewall,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdateFirewall = (projectId: string, firewallId: string) =>
  useProjectWrite((body: UpdateFirewallRequest) =>
    api(`/projects/${projectId}/firewalls/${firewallId}`, {
      method: "PATCH",
      body,
      schema: Firewall,
    }),
  );

export const useSetFirewallRules = (projectId: string, firewallId: string) =>
  useProjectWrite((rules: FirewallRuleInput[]) =>
    api(`/projects/${projectId}/firewalls/${firewallId}/rules`, {
      method: "PUT",
      body: { rules },
      schema: Operation,
    }),
  );

export const useApplyFirewall = (projectId: string, firewallId: string) =>
  useProjectWrite((body: FirewallTargets) =>
    api(`/projects/${projectId}/firewalls/${firewallId}/actions/apply`, {
      method: "POST",
      body,
      schema: Operation,
    }),
  );

export const useRemoveFirewall = (projectId: string, firewallId: string) =>
  useProjectWrite((body: FirewallTargets) =>
    api(`/projects/${projectId}/firewalls/${firewallId}/actions/remove`, {
      method: "POST",
      body,
      schema: Operation,
    }),
  );

export const useDeleteFirewall = (projectId: string) =>
  useProjectWrite((firewallId: string) =>
    api(`/projects/${projectId}/firewalls/${firewallId}`, { method: "DELETE" }),
  );
